import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "483",
  "slug": "what-is-a-lagoon-how-coastal-lagoons-form",
  "title": "What Is a Lagoon? How Coastal Lagoons Form Behind the Shore",
  "excerpt": "Lagoons are calm pools of water cut off from the open sea by a thin barrier. Here is the difference between coastal and coral lagoons, how they form, and how to tell a lagoon from an estuary.",
  "date": "2026-06-19",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1617350142588-5bf391aab3e8?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjb2FzdGFsJTIwbGFnb29uJTIwd2F0ZXJ8ZW58MXwwfHx8MTc4MTg1Njc4Nnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A lagoon is a shallow body of water separated from a larger body of water by a narrow barrier such as a sandbar, barrier island, or coral reef. Coastal lagoons sit behind sandy barriers along the shore, while coral lagoons lie inside the ring of a reef or atoll.",
  "content": [
    {
      "type": "paragraph",
      "text": "Some of the most striking coastlines on Earth are not where the land meets the open ocean, but just behind it: a strip of calm, shallow water held back by a thin ribbon of sand or coral. These are lagoons, and they shape everything from the city of Venice to the turquoise rings of the Pacific atolls."
    },
    {
      "type": "heading",
      "text": "What Is a Lagoon?"
    },
    {
      "type": "paragraph",
      "text": "A lagoon is a shallow stretch of water that is separated from a larger body of water — usually the sea — by a low barrier. The barrier might be a sandbar, a chain of barrier islands, a spit, or a coral reef. Because the barrier blocks most of the open water's energy, lagoons are typically calm, shallow, and only loosely connected to the sea through narrow gaps called inlets or passes."
    },
    {
      "type": "paragraph",
      "text": "That partial connection gives lagoons their distinctive water chemistry. Seawater trickles in through the inlets, fresh water may arrive from rivers and rain, and strong sun in warm climates speeds up evaporation. The result can range from brackish to saltier than the open ocean, depending on the local balance."
    },
    {
      "type": "heading",
      "text": "Coastal Lagoons"
    },
    {
      "type": "paragraph",
      "text": "Coastal lagoons form where waves and currents pile up sand parallel to the shore, building barrier islands or long sandbars a little way out from the land. The strip of water trapped behind that barrier becomes the lagoon. Venice sits in one of the most famous examples, sheltered from the Adriatic by long sandy islands. The Curonian Lagoon on the Baltic and the vast Lagoa dos Patos in southern Brazil are others. Because they are sheltered and rich in nutrients, coastal lagoons are some of the most productive habitats on Earth, full of fish, shellfish and migrating birds."
    },
    {
      "type": "heading",
      "text": "Coral Lagoons and Atolls"
    },
    {
      "type": "paragraph",
      "text": "The other great family of lagoons is built not by sand but by living coral. As a coral reef grows upward around an island or along a coast, it encloses a band of shallow water between the reef and the land — a reef lagoon. In an atoll, a ring-shaped coral reef surrounds a central lagoon where an island once stood before it sank or eroded away. These coral lagoons are the glowing turquoise circles you see in tropical satellite imagery, their colour coming from sunlight bouncing off pale sand in clear, shallow water."
    },
    {
      "type": "heading",
      "text": "Lagoon or Estuary?"
    },
    {
      "type": "paragraph",
      "text": "Lagoons are easy to confuse with estuaries, because both are sheltered coastal waters where land and sea mingle. The difference is what defines them:"
    },
    {
      "type": "list",
      "items": [
        "An estuary is the mouth of a river, where a strong flow of fresh water meets and mixes with the tide. It is defined by the river.",
        "A lagoon is defined by its barrier. It may receive little or no river water, and its character comes from being penned in behind a sandbar or reef.",
        "Estuaries are usually strongly brackish; lagoons can be brackish, fully marine, or even saltier than the sea depending on evaporation and inflow."
      ]
    },
    {
      "type": "heading",
      "text": "Reading Lagoons From the Map"
    },
    {
      "type": "paragraph",
      "text": "From above, a lagoon almost announces itself: a calm, often vividly coloured band of water with a thin line of land or reef holding back the darker open sea. Spot the barrier, find the inlets cutting through it, and you have identified a lagoon. It is one more piece of coastal grammar that turns a random shoreline into a place you can actually read — exactly the kind of clue that helps when you are trying to work out where in the world you have just landed in EarthGuessr."
    }
  ]
};
